import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { fbt } from 'fbtee'
import { MapPin, ChevronDown } from 'lucide-react'
import { cn } from '../lib/utils'
import { regionLabel } from '../lib/regionLabel'

/**
 * The area whose events are being shown, always visible at the top of Home and
 * the browse pages. It states the current region rather than waiting to be
 * discovered in Settings — under `auto` the region can change on its own when
 * the user travels, and a list that reshuffled itself only makes sense if
 * something on screen says which area it is showing.
 *
 * There is deliberately no "unset" copy: an uncovered timezone resolves to
 * `everywhere`, which is a real choice, not a prompt to go configure something.
 *
 * Spreads the rest of its props so it can be a Radix `asChild` trigger for
 * RegionPicker.
 */
export const LocationChip = forwardRef<
  HTMLButtonElement,
  { regionId: string; compact?: boolean } & ButtonHTMLAttributes<HTMLButtonElement>
>(function LocationChip({ regionId, compact, className, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      data-testid="location-chip"
      aria-label={fbt(
        'Change location',
        'Accessible label for the chip that changes which area events are shown for',
      ).toString()}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 -ml-2.5',
        'text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer',
        className,
      )}
      {...rest}
    >
      <MapPin className="size-3.5 shrink-0" />
      <span className="font-medium truncate">{regionLabel(regionId)}</span>
      {!compact && <ChevronDown className="size-3.5 shrink-0 opacity-60" />}
    </button>
  )
})
